<script lang="ts" setup>
import type { RfqTabState } from '@forgd/liquidity.forgd.com/layers/rfq/composables/useRfqTabState'
import type { MarketMakerRequestStatusEnumType } from '@forgd/shared/enums'
import type { Component } from 'vue'
import { getOutcomeTabState, getQuoteTabState } from '@forgd/liquidity.forgd.com/layers/rfq/composables/useRfqTabState'
import { MarketMakerRequestStatusEnum as RfqStatusEnum } from '@forgd/shared/enums'
import { CheckCircleIcon, LockClosedIcon, LockOpenIcon } from '@heroicons/vue/24/outline'

interface Tab {
  label: string
  to: string
  state: RfqTabState
}

interface Props {
  rfqId: string
  status?: MarketMakerRequestStatusEnumType
  isReopenedForMe?: boolean
  currentRound?: number
}

const props = defineProps<Props>()
const route = useRoute()

const iconMap: Record<RfqTabState, Component | undefined> = {
  disabled: undefined,
  locked: LockClosedIcon,
  unlocked: LockOpenIcon,
  completed: CheckCircleIcon,
}

function getTabClass(tab: Tab) {
  if (route.path === tab.to) {
    return 'border-b-2 border-accent text-accent'
  }

  if (tab.state === 'disabled' || tab.state === 'locked') {
    return 'text-disabled/30 cursor-not-allowed'
  }

  return 'text-secondary hover:text-accent'
}

const tabs = computed(() => [
  { label: 'Round 1: My Quotes', to: `/${props.rfqId}/round-1-my-quotes`, state: getQuoteTabState(props.status, RfqStatusEnum.RoundOneWaitingForQuotes, RfqStatusEnum.RoundOneQuotesInReview, 1, props.isReopenedForMe, props.currentRound) },
  { label: 'Round 1: Outcome', to: `/${props.rfqId}/round-1-outcome`, state: getOutcomeTabState(props.status, RfqStatusEnum.RoundOneQuotesAccepted, RfqStatusEnum.RoundOneWaitingForQuotes) },
  { label: 'Round 2: My Quotes', to: `/${props.rfqId}/round-2-my-quotes`, state: getQuoteTabState(props.status, RfqStatusEnum.RoundTwoWaitingForQuotes, RfqStatusEnum.RoundTwoQuotesInReview, 2, props.isReopenedForMe, props.currentRound) },
  { label: 'Round 2: Outcome', to: `/${props.rfqId}/round-2-outcome`, state: getOutcomeTabState(props.status, RfqStatusEnum.RoundTwoQuotesAccepted, RfqStatusEnum.RoundTwoWaitingForQuotes) },
])
</script>

<template>
  <nav aria-label="RFQ Details Navigation" class="flex items-center gap-10">
    <template v-for="tab in tabs" :key="tab.to">
      <NuxtLink
        v-if="tab.state === 'unlocked' || tab.state === 'completed'"
        :to="tab.to"
        class="flex items-center gap-1.5 py-5 text-xs whitespace-nowrap transition-colors font-semibold"
        :class="getTabClass(tab)"
      >
        <component :is="iconMap[tab.state]" v-if="iconMap[tab.state]" class="size-4 shrink-0" aria-hidden="true" />
        <span>{{ tab.label }}</span>
      </NuxtLink>
      <span
        v-else
        class="flex items-center gap-1.5 py-5 text-xs whitespace-nowrap transition-colors font-semibold"
        :class="getTabClass(tab)"
      >
        <component :is="iconMap[tab.state]" v-if="iconMap[tab.state]" class="size-4 shrink-0" aria-hidden="true" />
        <span>{{ tab.label }}</span>
      </span>
    </template>
  </nav>
</template>
